import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    IconChevronLeft, IconLoader2, IconUser, IconBriefcase,
    IconCalendarEvent, IconChartBar, IconClock, IconPlaneTilt,
    IconMoneybag, IconAlertCircle, IconBuilding, IconMail,
    IconHash, IconChevronDown, IconChevronUp, IconFileText,
    IconAlertTriangle,
} from '@tabler/icons-react';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useEmployeeSnapshot } from '../../hooks/useEmployees';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(d) {
    if (!d) return '—';
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_MAP = {
    approved: 'bg-success/10 text-success border border-success/30',
    submitted: 'bg-sky-500/10 text-sky-500 border border-sky-500/30',
    rejected: 'bg-danger/10 text-danger border border-danger/30',
    pending: 'bg-amber-500/10 text-amber-500 border border-amber-500/30',
    accepted: 'bg-success/10 text-success border border-success/30',
    draft: 'bg-bg-light text-text-secondary border border-border',
    paid: 'bg-purple-500/10 text-purple-500 border border-purple-500/30',
    active: 'bg-success/10 text-success border border-success/30',
    open: 'bg-amber-500/10 text-amber-500 border border-amber-500/30',
};

function StatusPill({ status }) {
    const cls = STATUS_MAP[status?.toLowerCase()] || 'bg-bg-light text-text-secondary border border-border';
    return (
        <span className={`px-8 py-2 rounded-full text-[11px] font-semibold capitalize whitespace-nowrap ${cls}`}>
            {status || '—'}
        </span>
    );
}

const AVATAR_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4', '#8b5cf6'];

function Avatar({ name, size = 48 }) {
    const initials = name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
    const bg = AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
    return (
        <div
            style={{ backgroundColor: bg, width: size, height: size, fontSize: size * 0.35 }}
            className="rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
        >
            {initials}
        </div>
    );
}

function QuickStat({ icon: Icon, label, value, accent }) {
    return (
        <div className="bg-bg-dark rounded-xl shadow-light border border-border p-20 flex items-center gap-15">
            <div className="w-50 h-50 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}18` }}>
                <Icon size={22} style={{ color: accent }} />
            </div>
            <div className="min-w-0">
                <p className="text-text-secondary text-xs uppercase tracking-wider mb-4">{label}</p>
                <p className="text-[22px]/[26px] font-bold truncate">{value ?? '—'}</p>
            </div>
        </div>
    );
}

function Card({ title, icon: Icon, color = '#6366f1', action, children }) {
    return (
        <div className="bg-bg-dark rounded-xl shadow-light border border-border overflow-hidden">
            <div className="px-20 py-12 border-b border-border flex items-center justify-between gap-8">
                <div className="flex items-center gap-8">
                    <Icon size={14} style={{ color }} />
                    <h3 className="text-sm font-bold text-text-main">{title}</h3>
                </div>
                {action}
            </div>
            <div className="p-20">{children}</div>
        </div>
    );
}

function InfoRow({ label, value, icon: Icon }) {
    return (
        <div className="flex items-center justify-between py-8 border-b border-border/50 last:border-0">
            <div className="flex items-center gap-6">
                {Icon && <Icon size={12} className="text-text-secondary flex-shrink-0" />}
                <span className="text-xs text-text-secondary">{label}</span>
            </div>
            <span className="text-sm font-semibold text-text-main capitalize text-right max-w-[55%] truncate">{value || '—'}</span>
        </div>
    );
}

function SectionLabel({ children }) {
    return <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-8">{children}</p>;
}

function EmptyState({ text }) {
    return <p className="text-xs text-text-secondary text-center py-15">{text}</p>;
}

const KPI_LIMIT = 5;

export default function EmployeeSnapshot() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [kpiExpanded, setKpiExpanded] = useState(false);

    const { data, isLoading, isError } = useEmployeeSnapshot(id);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-10">
                <IconLoader2 size={28} className="animate-spin text-primary" />
                <p className="text-sm text-text-secondary">Loading employee snapshot...</p>
            </div>
        );
    }

    if (isError || !data) {
        return (
            <div className="p-20">
                <Breadcrumb title="Employee Snapshot" path="Employees / Snapshot" />
                <div className="bg-bg-dark rounded-xl border border-border p-40 flex flex-col items-center gap-10 text-center">
                    <div className="w-50 h-50 rounded-full bg-danger/10 flex items-center justify-center">
                        <IconAlertCircle size={22} className="text-danger" />
                    </div>
                    <p className="font-semibold text-sm">Could not load employee data</p>
                    <p className="text-text-secondary text-xs">The employee record may not be available.</p>
                    <button onClick={() => navigate(-1)} className="btn btn-outline-secondary mt-5 text-sm">Go Back</button>
                </div>
            </div>
        );
    }

    const { employee, leave, kpi, timesheets, travel, payroll } = data;
    const snapshotDocs = employee?.documents;

    const totalLeaveAvailable = leave?.balances?.reduce((sum, b) => sum + (b.available || 0), 0) ?? 0;
    const currentTimesheetStatus = timesheets?.current?.status || timesheets?.recent?.[0]?.status || 'No data';
    const travelCount = travel?.recent_requests?.length ?? 0;

    const kpiSections = kpi?.sections || [];
    const visibleKpi = kpiExpanded ? kpiSections : kpiSections.slice(0, KPI_LIMIT);
    const kpiHidden = kpiSections.length - KPI_LIMIT;
    const kpiStatusCounts = kpiSections.reduce((acc, s) => {
        const key = s.status?.toLowerCase() || 'unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});

    return (
        <div>
            <Breadcrumb title="Employee Snapshot" path="Employees / Snapshot" />

            <button
                onClick={() => navigate(-1)}
                className="btn btn-outline-secondary flex items-center gap-6 mb-15"
            >
                <IconChevronLeft size={16} /> Back
            </button>

            {/* Profile Header */}
            <div className="bg-bg-dark rounded-xl shadow-light border border-border p-20 mb-20 flex items-center gap-15 flex-wrap">
                <Avatar name={employee?.full_name} size={56} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-10 flex-wrap mb-5">
                        <h2 className="text-[18px] font-bold text-text-main">{employee?.full_name}</h2>
                        <StatusPill status={employee?.employment_status} />
                    </div>
                    <p className="text-sm text-text-secondary">
                        {employee?.job_title}{employee?.department ? ` · ${employee?.department}` : ''}
                    </p>
                </div>
                <div className="flex items-center gap-15 flex-wrap shrink-0">
                    {employee?.email && (
                        <div className="flex items-center gap-5 text-xs text-text-secondary">
                            <IconMail size={13} />
                            {employee.email}
                        </div>
                    )}
                    {employee?.employee_no && (
                        <div className="flex items-center gap-5 text-xs font-mono bg-bg-light border border-border px-8 py-4 rounded">
                            <IconHash size={11} className="text-text-secondary" />
                            {employee.employee_no}
                        </div>
                    )}
                </div>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-15 mb-20">
                <QuickStat icon={IconCalendarEvent} label="Annual Leave" value="26 days" accent="#0ea5e9" />
                <QuickStat icon={IconClock} label="Timesheet" value={currentTimesheetStatus} accent="#f59e0b" />
                <QuickStat icon={IconChartBar} label="KPI Sections" value={kpiSections.length} accent="#6366f1" />
                <QuickStat icon={IconPlaneTilt} label="Travel Requests" value={travelCount} accent="#14b8a6" />
            </div>

            {/* Full-width detail cards */}
            <div className="space-y-15">

                {/* Employment Details — 3-col grid inside */}
                <Card title="Employment Details" icon={IconUser} color="#6366f1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-40">
                        <InfoRow label="Department" value={employee?.department} icon={IconBuilding} />
                        <InfoRow label="Unit" value={employee?.unit} icon={IconBuilding} />
                        <InfoRow label="Job Title" value={employee?.job_title} icon={IconBriefcase} />
                        <InfoRow label="Line Manager" value={employee?.line_manager} icon={IconUser} />
                        <InfoRow label="Hire Date" value={formatDate(employee?.hire_date)} icon={IconCalendarEvent} />
                        <InfoRow label="Status" value={employee?.employment_status} />
                    </div>
                </Card>

                {/* Leave Balances — balances in multi-col, recent requests in table */}
                <Card title="Leave Balances" icon={IconCalendarEvent} color="#0ea5e9">
                    {leave?.balances?.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-40 gap-y-15">
                            {leave.balances.map((b, i) => (
                                <div key={i}>
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="text-xs font-semibold text-text-main truncate max-w-[65%]">{b.leave_type}</span>
                                        <span className="text-xs text-text-secondary">{b.available}/{b.allocated}d</span>
                                    </div>
                                    <div className="w-full bg-bg-light rounded-full h-[5px]">
                                        <div
                                            className="bg-sky-500 h-[5px] rounded-full transition-all"
                                            style={{ width: `${Math.min((b.available / b.allocated) * 100, 100)}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState text="No leave balance data." />
                    )}

                    {leave?.recent_requests?.length > 0 && (
                        <div className="mt-20 pt-15 border-t border-border">
                            <SectionLabel>Recent Requests</SectionLabel>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-40">
                                {leave.recent_requests.slice(0, 6).map((r, i) => (
                                    <div key={i} className="flex items-center justify-between py-7 border-b border-border/50 last:border-0">
                                        <span className="text-xs text-text-main truncate max-w-[55%]">{r.leave_type__name} · {r.days_requested}d</span>
                                        <StatusPill status={r.status} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Card>

                {/* Timesheets — current + recent side by side */}
                <Card title="Timesheets" icon={IconClock} color="#f59e0b">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-40">
                        {/* Current period */}
                        <div>
                            <SectionLabel>Current Period</SectionLabel>
                            {timesheets?.current ? (
                                <div className="flex items-center justify-between bg-bg-light rounded-lg px-15 py-10">
                                    <span className="text-sm font-semibold text-text-main">
                                        {months[timesheets.current.month - 1]} {timesheets.current.year}
                                    </span>
                                    <StatusPill status={timesheets.current.status} />
                                </div>
                            ) : (
                                <p className="text-xs text-text-secondary">No current timesheet.</p>
                            )}
                        </div>

                        {/* Recent history */}
                        <div>
                            <SectionLabel>Recent History</SectionLabel>
                            {timesheets?.recent?.length > 0 ? (
                                <div className="space-y-4">
                                    {timesheets.recent.slice(0, 5).map((t, i) => (
                                        <div key={i} className="flex items-center justify-between py-7 border-b border-border/50 last:border-0">
                                            <span className="text-sm text-text-main">{months[t.month - 1]} {t.year}</span>
                                            <StatusPill status={t.status} />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-text-secondary">No recent timesheets.</p>
                            )}
                        </div>
                    </div>
                </Card>

                {/* KPI / Appraisal */}
                <Card title="KPI / Appraisal" icon={IconChartBar} color="#6366f1">
                    {kpiSections.length > 0 ? (
                        <>
                            {/* Status summary */}
                            <div className="flex items-center gap-6 flex-wrap mb-15 pb-15 border-b border-border">
                                {Object.entries(kpiStatusCounts).map(([status, count]) => (
                                    <span key={status} className={`px-8 py-2 rounded-full text-[11px] font-semibold capitalize whitespace-nowrap ${STATUS_MAP[status] || 'bg-bg-light text-text-secondary border border-border'}`}>
                                        {count} {status}
                                    </span>
                                ))}
                            </div>

                            {/* Sections in multi-col grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-40">
                                {visibleKpi.map((s, i) => (
                                    <div key={i} className="flex items-center justify-between py-8 border-b border-border/50 last:border-0">
                                        <div className="min-w-0 flex-1 pr-10">
                                            <p className="text-sm font-semibold text-text-main truncate">{s.section_name}</p>
                                            <p className="text-xs text-text-secondary mt-2 truncate">{s.cycle_name}</p>
                                        </div>
                                        <StatusPill status={s.status} />
                                    </div>
                                ))}
                            </div>

                            {kpiSections.length > KPI_LIMIT && (
                                <button
                                    onClick={() => setKpiExpanded(prev => !prev)}
                                    className="mt-12 w-full flex items-center justify-center gap-5 text-xs font-semibold text-primary hover:opacity-80 py-8 rounded-lg hover:bg-primary/5 transition-colors"
                                >
                                    {kpiExpanded ? (
                                        <><IconChevronUp size={13} /> Show less</>
                                    ) : (
                                        <><IconChevronDown size={13} /> Show {kpiHidden} more section{kpiHidden !== 1 ? 's' : ''}</>
                                    )}
                                </button>
                            )}
                        </>
                    ) : (
                        <EmptyState text="No KPI data." />
                    )}
                </Card>

                {/* Travel + Payslips — same row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-15">

                    {/* Travel Requests */}
                    <Card title="Travel Requests" icon={IconPlaneTilt} color="#14b8a6">
                        {travel?.recent_requests?.length > 0 ? (
                            <div className="space-y-4">
                                {travel.recent_requests.slice(0, 5).map((r, i) => (
                                    <div key={i} className="flex items-center justify-between py-8 border-b border-border/50 last:border-0">
                                        <span className="text-sm text-text-main truncate max-w-[55%]">{r.purpose || `Request ${i + 1}`}</span>
                                        <StatusPill status={r.status} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <EmptyState text="No travel requests." />
                        )}
                    </Card>

                    {/* Payslips */}
                    <Card title="Recent Payslips" icon={IconMoneybag} color="#22c55e">
                        {payroll?.recent_payslips?.length > 0 ? (
                            <div className="space-y-8">
                                {payroll.recent_payslips.slice(0, 4).map((p, i) => (
                                    <div key={i} className="py-8 border-b border-border/50 last:border-0">
                                        <div className="flex items-center justify-between mb-4">
                                            <span className="text-sm font-semibold text-text-main truncate max-w-[55%]">{p.payroll_run__label}</span>
                                            <span className="text-sm text-success font-bold">
                                                {Number(p.net_pay).toLocaleString('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 })}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-text-secondary">Gross</span>
                                            <span className="text-xs text-text-secondary">
                                                {Number(p.gross_pay).toLocaleString('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 })}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <EmptyState text="No payslip data." />
                        )}
                    </Card>

                </div>

                {/* Documents */}
                <Card
                    title="Documents"
                    icon={IconFileText}
                    color="#6366f1"
                    action={
                        <div className="flex items-center gap-8">
                            <button
                                onClick={() => navigate(`/documents/upload?employee_id=${id}`)}
                                className="text-xs font-semibold text-primary hover:underline"
                            >
                                Upload
                            </button>
                            <span className="text-border">|</span>
                            <button
                                onClick={() => navigate(`/documents/employee/${id}`)}
                                className="text-xs font-semibold text-primary hover:underline"
                            >
                                View All
                            </button>
                        </div>
                    }
                >
                    {snapshotDocs ? (
                        <>
                            <div className="flex flex-wrap gap-12 mb-15">
                                <div className="flex-1 min-w-[80px] bg-bg-light rounded-lg px-12 py-10 text-center">
                                    <p className="text-[20px] font-bold text-text-primary">{snapshotDocs.total ?? 0}</p>
                                    <p className="text-[10px] text-text-secondary mt-1">Total</p>
                                </div>
                                {snapshotDocs.expiring_soon > 0 && (
                                    <div className="flex-1 min-w-[80px] bg-warning/10 rounded-lg px-12 py-10 text-center">
                                        <p className="text-[20px] font-bold text-warning">{snapshotDocs.expiring_soon}</p>
                                        <p className="text-[10px] text-warning/80 mt-1">Expiring soon</p>
                                    </div>
                                )}
                            </div>
                            {snapshotDocs.recent?.length > 0 ? (
                                <div className="flex flex-col gap-0 divide-y divide-border">
                                    {snapshotDocs.recent.slice(0, 4).map((doc, i) => (
                                        <div key={i} className="flex items-center gap-8 py-8">
                                            <IconFileText size={13} className="text-text-secondary flex-shrink-0" />
                                            <div className="flex-grow min-w-0">
                                                <p className="text-[12px] font-medium text-text-primary truncate">{doc.title}</p>
                                                <p className="text-[10px] text-text-secondary truncate">{doc.original_filename}</p>
                                            </div>
                                            {doc.expires_on && (
                                                <span className="text-[10px] text-warning flex-shrink-0 flex items-center gap-3">
                                                    <IconAlertTriangle size={10} /> {new Date(doc.expires_on).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <EmptyState text="No documents uploaded yet." />
                            )}
                        </>
                    ) : (
                        <EmptyState text="No document data available." />
                    )}
                </Card>

            </div>
        </div>
    );
}
